import { useAuthentication } from "@myinnsworth/hooks/authenticationHook";
import Breadcrumbs from "../../components/breadcrumb/breadcrumb";
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import clsx from "clsx";
import { useAppStore } from "@myinnsworth/hooks/appStoreHook";
import Modal from "components/modal/modal";
import { useState } from "react";
import { authenticationHttpService, contactHttpService } from "@myinnsworth/httpServices";
import axios, { AxiosError } from "axios";
import Select from "components/select/select";

const profileSchema = yup.object({
    email: yup.string().email('Invalid email address').required('Invalid email address'),
    forename: yup.string().required('Forename is required'),
    surname: yup.string().required('Surname is required'),
    companyName: yup.string().required('Company name is required'),
    jobTitle: yup.string().required('Job title is required'),
    phoneNumber: yup.string(),
    addressLine1: yup.string(),
    addressLine2: yup.string(),
    area: yup.string(),
    city: yup.string(),
    postcode: yup.string(),
    countryId: yup.number().required('Invalid country')
}).required();

interface IProfileSchema {
    email: string;
    forename: string;
    surname: string;
    companyName: string;
    jobTitle: string;
    phoneNumber?: string;
    addressLine1?: string;
    addressLine2?: string;
    area?: string;
    city?: string;
    postcode?: string;
    countryId: number;
}

enum UpdateProfileState {
    None,
    Success,
    InProgress
}

export default function ProfileSetting() {
    const auth = useAuthentication();
    const appStore = useAppStore();
    const [state, setState] = useState<UpdateProfileState>(UpdateProfileState.None);

    const { register: registerInput, handleSubmit, setValue, clearErrors, watch, setError, formState: { errors } } = useForm<IProfileSchema>({
        resolver: yupResolver(profileSchema),
        defaultValues: {
            forename: auth.contact?.forename,
            surname: auth.contact?.surname,
            companyName: auth.contact?.companyName,
            jobTitle: auth.contact?.jobTitle,
            email: auth.user?.email,
            phoneNumber: auth.contact?.phoneNumber,
            addressLine1: auth.contact?.address?.address1,
            addressLine2: auth.contact?.address?.address2,
            area: auth.contact?.address?.area,
            city: auth.contact?.address?.city,
            postcode: auth.contact?.address?.zipCode,
            countryId: auth.contact?.address?.country?.id
        }
    });
    const countryWatch = watch('countryId');

    const breadcrumbs = [
        { label: 'Dashboard', url: '/' },
        { label: 'Profile Settings', url: '/settings/profile' },
    ];

    const onCloseSuccess = async () => {
        try {
            await authenticationHttpService.signInWithToken();
        } finally {
            setState(UpdateProfileState.None);
        }
    };

    const onSubmit = async (formData: IProfileSchema) => {
        clearErrors();
        try {
            setState(UpdateProfileState.InProgress);
            await contactHttpService.updateProfile(formData);
            setState(UpdateProfileState.Success);

        } catch (ex) {
            if (axios.isAxiosError(ex)) {
                const error = ex as AxiosError;
                switch (error.response!.status) {
                    case 400: { //400 - Invalid email address
                        setError('email', { message: 'Invalid email address' });
                        break;
                    }
                    case 409: { //409 - Email aready exists
                        setError('email', { message: 'Email address already registered' });
                        break;
                    }
                }
            }
            setState(UpdateProfileState.None);
        }
    };

    return (
        <>
            <Breadcrumbs items={breadcrumbs} />

            <div className="spacing-m"></div>

            <label className="normal">Edit contact details</label>

            <div className="spacing-m"></div>

            <section className="form-container">

                <form onSubmit={handleSubmit(onSubmit)} noValidate>

                    <div className="grid col-2">
                        <div className={clsx('form-control', { 'error': errors.forename })}>
                            <label >Forename</label>
                            <input {...registerInput('forename')} type="text" />
                            {errors.forename && (<p>{errors.forename.message}</p>)}
                        </div>

                        <div className={clsx('form-control', { 'error': errors.surname })}>
                            <label >Surname</label>
                            <input {...registerInput('surname')} type="text" />
                            {errors.surname && (<p>{errors.surname.message}</p>)}
                        </div>

                        <div className={clsx('form-control', { 'error': errors.companyName })}>
                            <label >Company name</label>
                            <input {...registerInput('companyName')} type="text" />
                            {errors.companyName && (<p>{errors.companyName.message}</p>)}
                        </div>

                        <div className={clsx('form-control', { 'error': errors.jobTitle })}>
                            <label >Job title</label>
                            <input {...registerInput('jobTitle')} type="text" />
                            {errors.jobTitle && (<p>{errors.jobTitle.message}</p>)}
                        </div>

                        <div className={clsx('form-control', { 'error': errors.phoneNumber })}>
                            <label >Phone number</label>
                            <input {...registerInput('phoneNumber')} type="text" />
                            {errors.phoneNumber && (<p>{errors.phoneNumber.message}</p>)}
                        </div>

                        <div className={clsx('form-control', { 'error': errors.email })}>
                            <label >Email address</label>
                            <input {...registerInput('email')} type="email" />
                            {errors.email && (<p>{errors.email.message}</p>)}
                        </div>

                        <div className={clsx('form-control', { 'error': errors.addressLine1 })}>
                            <label >Address line 1</label>
                            <input {...registerInput('addressLine1')} type="text" />
                            {errors.addressLine1 && (<p>{errors.addressLine1.message}</p>)}
                        </div>

                        <div className={clsx('form-control', { 'error': errors.addressLine2 })}>
                            <label >Address line 2</label>
                            <input {...registerInput('addressLine2')} type="text" />
                            {errors.addressLine2 && (<p>{errors.addressLine2.message}</p>)}
                        </div>

                        <div className={clsx('form-control', { 'error': errors.area })}>
                            <label >County</label>
                            <input {...registerInput('area')} type="text" />
                            {errors.area && (<p>{errors.area.message}</p>)}
                        </div>

                        <div className={clsx('form-control', { 'error': errors.city })}>
                            <label >City</label>
                            <input {...registerInput('city')} type="text" />
                            {errors.city && (<p>{errors.city.message}</p>)}
                        </div>

                        <div className={clsx('form-control', { 'error': errors.postcode })}>
                            <label >Postcode</label>
                            <input {...registerInput('postcode')} type="text" />
                            {errors.postcode && (<p>{errors.postcode.message}</p>)}
                        </div>

                        <div className={clsx('form-control', { 'error': errors.countryId })}>
                            <label >Country</label>
                            {/* <select {...registerInput('countryId')}>
                                {appStore.countries?.map(x => <option selected={x.id === countryWatch} key={x.id} value={x.id}>{x.name}</option>)}
                            </select> */}
                            <Select props={registerInput('countryId')} 
                                    optionList={appStore.countries} 
                                    selectedId={countryWatch}
                                    onSelectedChanged={item => {
                                        setValue('countryId', item.id as number);
                                    }}></Select>
                            {errors.countryId && (<p>{errors.countryId.message}</p>)}
                        </div>
                    </div>

                    <div className="flex justify-right pt-m">
                        <button type="submit" className="btn primary">Save changes</button>
                    </div>
                </form>

            </section>

            {state === UpdateProfileState.Success && (
                <Modal
                    title="Profile updated"
                    children={<>
                        Your profile was successfully updated
                        <div className="flex justify-right">
                            <button title="Close" className="btn primary medium" onClick={onCloseSuccess}>Close</button>
                        </div>
                    </>}
                    closeOnCoverClick={false}
                    onClose={onCloseSuccess} />
            )}
        </>
    )
}